import { useEffect, useState, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Plus, Edit3, Upload, X, Loader2, Users, RefreshCw, CalendarDays } from 'lucide-react';
import { dashboardApi } from '@/services/dashboardApi';
import { fmtMoney } from '@/lib/format';
import { Segmented, btnGold, btnGhost } from '@/components/dashboard/kit';

/*
  BootcampManager — ediciones del bootcamp dentro del panel admin.
  Sustituye a la antigua página /admin/bootcamp (otro login). Las inscripciones llegan desde el panel
  de miembro (Premium: incluida; resto: pago suelto con Stripe) y se ven y gestionan en
  Personas › Inscripciones («Ver inscritos»), con la ficha de cada persona.
*/

const inputCls = 'w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#c9a84c]/40';
const labelCls = 'text-[11px] uppercase tracking-wider text-gray-400 mb-1 block font-semibold';
const today = () => new Date().toISOString().slice(0, 10);
const fmtDate = (d) => (d ? new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

const EDITION_STATUS = { draft: 'Borrador', published: 'Publicada', archived: 'Archivada' };

function EditionModal({ edition, onClose, onSaved }) {
  const isEditing = !!edition?.id;
  const [form, setForm] = useState({
    number: edition?.number ?? '', title: edition?.title || '', description: edition?.description || '',
    start_date: (edition?.start_date || '').slice(0, 10), end_date: (edition?.end_date || '').slice(0, 10),
    location: edition?.location || 'Amaroo', city: edition?.city || '', capacity: edition?.capacity ?? 20,
    price: edition?.price ?? '', currency: edition?.currency || 'EUR', status: edition?.status || 'draft',
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.start_date || !form.end_date) return toast.error('Título y fechas son obligatorios');
    if (form.end_date < form.start_date) return toast.error('La fecha de fin no puede ser anterior a la de inicio');
    const payload = {
      ...form,
      number: form.number === '' ? null : Number(form.number),
      capacity: Number(form.capacity) || 0,
      price: form.price === '' ? null : Number(form.price),
    };
    setSaving(true);
    try {
      if (isEditing) await dashboardApi.bootcampUpdateEdition(edition.id, payload);
      else await dashboardApi.bootcampCreateEdition(payload);
      toast.success(isEditing ? 'Edición actualizada' : 'Edición creada');
      onSaved();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      await dashboardApi.bootcampUploadFlyer(edition.id, file);
      toast.success('Flyer subido');
      onSaved(false);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 overflow-y-auto" role="dialog" aria-modal="true">
      <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.15 }}
        className="bg-[#0d1829] border border-white/10 rounded-2xl w-full max-w-2xl shadow-2xl my-auto">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <h3 className="text-white font-semibold">{isEditing ? `Editar: ${edition.title}` : 'Nueva edición del bootcamp'}</h3>
          <button onClick={onClose} aria-label="Cerrar"><X size={18} className="text-gray-400 hover:text-white" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4 max-h-[72vh] overflow-y-auto">
          {isEditing && (
            <div className="flex items-center gap-3 rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => upload(e.target.files[0])} />
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/[0.05] hover:bg-white/[0.08] text-sm text-white border border-white/10 disabled:opacity-50">
                {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {edition.flyer_url ? 'Reemplazar flyer' : 'Subir flyer'}
              </button>
              <span className="text-[11px] text-gray-400">JPG, PNG o WebP</span>
            </div>
          )}
          <div className="grid grid-cols-4 gap-3">
            <div className="col-span-1"><label className={labelCls}>Nº</label><input type="number" value={form.number} onChange={set('number')} className={inputCls} /></div>
            <div className="col-span-3"><label className={labelCls}>Título *</label><input value={form.title} onChange={set('title')} className={inputCls} placeholder="Bootcamp Broker Digital #7" /></div>
          </div>
          <div><label className={labelCls}>Descripción</label><textarea rows={2} value={form.description} onChange={set('description')} className={`${inputCls} resize-none`} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Inicio *</label><input type="date" value={form.start_date} onChange={set('start_date')} className={inputCls} /></div>
            <div><label className={labelCls}>Fin *</label><input type="date" value={form.end_date} onChange={set('end_date')} className={inputCls} /></div>
            <div><label className={labelCls}>Lugar</label><input value={form.location} onChange={set('location')} className={inputCls} /></div>
            <div><label className={labelCls}>Ciudad</label><input value={form.city} onChange={set('city')} className={inputCls} /></div>
            <div><label className={labelCls}>Plazas *</label><input type="number" min="0" value={form.capacity} onChange={set('capacity')} className={inputCls} /></div>
            <div><label className={labelCls}>Estado</label>
              <select value={form.status} onChange={set('status')} className={inputCls}>
                {Object.entries(EDITION_STATUS).map(([k, v]) => <option key={k} value={k} className="bg-[#0d1829]">{v}</option>)}
              </select>
            </div>
          </div>
          <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
            <p className="text-sm text-white font-medium">Plaza suelta para Access y PRO</p>
            <p className="text-xs text-gray-400 mt-1 mb-3">Premium la tiene incluida. Si dejas el precio vacío, se cobra el precio estándar (897 € / 997 $).</p>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={labelCls}>Precio</label><input type="number" min="0" step="1" value={form.price} onChange={set('price')} className={inputCls} placeholder="Vacío = 897 € / 997 $ (precio estándar)" /></div>
              <div><label className={labelCls}>Moneda</label>
                <select value={form.currency} onChange={set('currency')} className={inputCls}>
                  <option value="EUR" className="bg-[#0d1829]">EUR</option><option value="USD" className="bg-[#0d1829]">USD</option>
                </select>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-white/10 text-sm text-gray-300 hover:text-white">Cancelar</button>
            <button type="submit" disabled={saving} className="px-4 py-2 rounded-lg bg-[#c9a84c] text-[#0a1628] text-sm font-semibold disabled:opacity-50">
              {saving ? 'Guardando…' : isEditing ? 'Guardar cambios' : 'Crear edición'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

export default function BootcampManager({ onShowRegistrations }) {
  const [editions, setEditions] = useState(null);
  const [modal, setModal] = useState(null);
  const [showPast, setShowPast] = useState(false);

  const load = useCallback(() => {
    dashboardApi.bootcampEditions().then((e) => setEditions(e || [])).catch((err) => { toast.error(err.message); setEditions([]); });
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!editions) return <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-[#c9a84c]" size={24} /></div>;

  const sorted = [...editions].sort((a, b) => String(a.start_date).localeCompare(String(b.start_date)));
  const upcoming = sorted.filter((e) => String(e.end_date || '').slice(0, 10) >= today());
  const past = sorted.filter((e) => String(e.end_date || '').slice(0, 10) < today()).reverse();
  const list = showPast ? past : upcoming;

  return (
    <div className="space-y-4" data-testid="bootcamp-manager">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Segmented tabs={[['proximas', 'Próximas', upcoming.length], ['pasadas', 'Pasadas', past.length]]}
          value={showPast ? 'pasadas' : 'proximas'} onChange={(v) => setShowPast(v === 'pasadas')} />
        <div className="flex gap-2">
          <button type="button" onClick={load} className={`${btnGhost} h-10 w-10 px-0`} aria-label="Recargar"><RefreshCw size={15} /></button>
          <button type="button" onClick={() => setModal({})} className={`${btnGold} h-10`} data-testid="bootcamp-new">
            <Plus size={15} /> Nueva edición
          </button>
        </div>
      </div>

      {showPast && <p className="text-xs text-gray-400">Las ediciones pasadas ya no aparecen a los miembros para inscribirse. Aquí quedan como histórico.</p>}

      {!list.length && (
        <div className="rounded-2xl border border-dashed border-white/[0.08] py-10 text-center text-sm text-gray-400">
          {showPast ? 'No hay ediciones pasadas.' : 'No hay ediciones próximas. Crea una y publícala para que los miembros puedan reservar.'}
        </div>
      )}

      {list.map((ed) => {
        const free = Math.max(0, (ed.capacity || 0) - (ed.approved_count || 0));
        return (
          <div key={ed.id} className="rounded-2xl border border-white/[0.06] bg-white/[0.02]">
            <div className="flex flex-wrap items-center gap-4 p-4">
              <div className="w-10 h-10 rounded-xl bg-[#c9a84c]/10 flex items-center justify-center"><CalendarDays size={17} className="text-[#c9a84c]" /></div>
              <div className="flex-1 min-w-[200px]">
                <p className="text-white font-medium">{ed.title}{showPast && <span className="ml-2 text-[11px] text-gray-400 uppercase tracking-wider">Celebrada</span>}</p>
                <p className="text-xs text-gray-400">{fmtDate(ed.start_date)} – {fmtDate(ed.end_date)} · {ed.location || '—'}{ed.city ? `, ${ed.city}` : ''}</p>
              </div>
              <div className="text-xs text-gray-400 tabular-nums">
                <span className="text-white">{ed.approved_count || 0}</span>/{ed.capacity} confirmadas · {free} libres
                {ed.pending_count ? <span className="ml-2 text-amber-200">{ed.pending_count} pendientes</span> : null}
              </div>
              <span className="text-[11px] uppercase tracking-wider text-gray-400">{EDITION_STATUS[ed.status] || ed.status}</span>
              <span className="text-xs text-gray-300">{ed.price ? fmtMoney(ed.price, ed.currency) : 'Precio estándar (897 € / 997 $)'}</span>
              <div className="flex gap-2">
                <button type="button" onClick={() => setModal(ed)} className="w-10 h-10 inline-flex items-center justify-center rounded-lg border border-white/10 text-gray-400 hover:text-white" aria-label={`Editar ${ed.title}`}><Edit3 size={15} /></button>
                <button type="button" onClick={() => onShowRegistrations(`bootcamp:${ed.id}`)} className="inline-flex items-center gap-1.5 min-h-[40px] rounded-lg border border-white/10 px-3 text-xs text-white hover:bg-white/[0.05]">
                  <Users size={13} /> Ver inscritos ({ed.total_registrations || 0})
                </button>
              </div>
            </div>
          </div>
        );
      })}

      {modal && <EditionModal edition={modal.id ? modal : null} onClose={() => setModal(null)} onSaved={(close = true) => { if (close) setModal(null); load(); }} />}
    </div>
  );
}

